import { Form, Switch, Select, Space, Row, Col, Flex, Button } from 'antd';
import {
  RobotOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  FullscreenOutlined,
} from '@ant-design/icons';
import { FunctionComponent, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AspectRatio, ThumbnailType } from '@monorepo/types';
import { aspectRatioOptions } from '../../video/video-player/video-player-settings';
import { FormSlider } from '@monorepo/react-components';
import SubtitlesModal, {
  SubtitlesConfigModalActions,
} from '../../video/subtitles/subtitles-modal';
import { useTranslation } from 'react-i18next';

interface VideoOptionsFormProps {
  form: UseFormReturn<any>;
}

export const VideoOptionsForm: FunctionComponent<VideoOptionsFormProps> = ({
  form,
}) => {
  const { t } = useTranslation();
  const thumbnailEnabled = form.watch('thumbnailConfig.enabled');
  const modalRef = useRef<SubtitlesConfigModalActions>(null);

  useEffect(() => {
    if (thumbnailEnabled) {
      form.setValue('thumbnailConfig.enableVoiceover', true, {
        shouldValidate: false,
      });
    } else {
      form.setValue('thumbnailConfig.enableVoiceover', false, {
        shouldValidate: false,
      });
    }
  }, [thumbnailEnabled]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <Space>
                <RobotOutlined />
                <span>{t('videoOptions.enableAiMotions')}</span>
              </Space>
            }
            tooltip={{
              title: t('videoOptions.enableAiMotionsTooltip'),
              placement: 'right',
              overlayStyle: { maxWidth: 300 },
              color: '#1677ff',
            }}
            style={{ marginBottom: 0 }}
          >
            <Switch
              checked={form.watch('options.enableAiMotions')}
              onChange={(checked) => {
                form.setValue('options.enableAiMotions', checked, {
                  shouldValidate: true,
                });
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <Space>
                <ThunderboltOutlined />
                <span>{t('videoOptions.videoSpeed')}</span>
              </Space>
            }
            tooltip={{
              title: t('videoOptions.videoSpeedTooltip'),
              placement: 'right',
              overlayStyle: { maxWidth: 300 },
              color: '#1677ff',
            }}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: 80 }}
              value={form.watch('playbackRate') || 1}
              onChange={(value) => {
                form.setValue('playbackRate', value, {
                  shouldValidate: true,
                });
              }}
              options={[
                { value: 1, label: '1.0x' },
                { value: 1.1, label: '1.1x' },
                { value: 1.2, label: '1.2x' },
                { value: 1.3, label: '1.3x' },
                { value: 1.4, label: '1.4x' },
                { value: 1.5, label: '1.5x' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <Space>
                <PictureOutlined />
                <span>{t('videoOptions.enableThumbnail')}</span>
              </Space>
            }
            tooltip={{
              title: t('videoOptions.enableThumbnailTooltip'),
              placement: 'right',
              overlayStyle: { maxWidth: 300 },
              color: '#1677ff',
            }}
            style={{ marginBottom: 0 }}
          >
            <Switch
              defaultChecked={true}
              checked={form.watch('thumbnailConfig.enabled')}
              onChange={(checked) => {
                form.setValue('thumbnailConfig.enabled', checked, {
                  shouldValidate: true,
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <Space>
                <FullscreenOutlined />
                <span>{t('videoOptions.aspectRatio')}</span>
              </Space>
            }
            tooltip={{
              title: t('videoOptions.aspectRatioTooltip'),
              placement: 'right',
              overlayStyle: { maxWidth: 300 },
              color: '#1677ff',
            }}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: 100 }}
              value={form.watch('aspectRatio') || AspectRatio.NineBySixteen}
              onChange={(value) => {
                form.setValue('aspectRatio', value, {
                  shouldValidate: true,
                });
              }}
              optionLabelProp="value"
              options={aspectRatioOptions.map((option) => ({
                value: option.value,
                label: (
                  <Flex align="center" gap={8}>
                    {option.icon}
                    {option.label}
                  </Flex>
                ),
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {thumbnailEnabled && (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('videoOptions.thumbnailType')}
              style={{ marginBottom: 0 }}
            >
              <Select
                defaultValue={ThumbnailType.TikTok}
                value={form.watch('thumbnailConfig.type')}
                onChange={(value) => {
                  form.setValue('thumbnailConfig.type', value, {
                    shouldValidate: true,
                  });
                }}
                options={[
                  { value: ThumbnailType.Reddit, label: 'Reddit' },
                  { value: ThumbnailType.TikTok, label: 'TikTok' },
                  { value: ThumbnailType.Text, label: 'Text' },
                ]}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={t('videoOptions.enableThumbnailVoiceover')}
              style={{ marginBottom: 0 }}
            >
              <Switch
                defaultChecked={true}
                checked={form.watch('thumbnailConfig.enableVoiceover')}
                onChange={(checked) => {
                  form.setValue('thumbnailConfig.enableVoiceover', checked, {
                    shouldValidate: true,
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={24} sm={12}>
          <FormSlider
            label={t('videoOptions.backgroundMusicVolume')}
            form={form}
            props={{
              min: 0,
              max: 2,
              step: 0.1,
              tooltip: { formatter: (value) => `${value}x` },
            }}
            controllerProps={{
              name: 'backgroundMusicVolume',
              defaultValue: 0.5,
              rules: {
                min: {
                  value: 0,
                  message: t('videoOptions.minVolumeError'),
                },
                max: {
                  value: 100,
                  message: t('videoOptions.maxVolumeError'),
                },
              },
            }}
          />
        </Col>
        <Col xs={24} sm={24}>
          <Button type="dashed" block onClick={() => modalRef.current?.open()}>
            {t('videoOptions.subtitlesConfig')}
          </Button>
        </Col>
      </Row>
      <SubtitlesModal ref={modalRef} form={form} />
    </Space>
  );
};
